/**
 * CalendlyDomain Service
 *
 * Domain service for Calendly appointment operations in the BiteWise healthcare provider platform.
 * Handles appointment fetching, creation, linking, and refresh operations with enhanced error handling,
 * retry logic, and request deduplication.
 *
 * Migrated from CalendlyAppointmentApiService to follow the new domain architecture pattern.
 *
 * @module services/domains/CalendlyDomain
 */

import { BaseDomain, ApiClient, tokenManager, requestManager, getLoggerWithPrefix } from '../shared';
import { PaginationService } from '../shared/PaginationService';
import { CalendlyAppointmentApiError } from '@/types/calendlyAppointments';
import type {
  CalendlyAppointment,
  CalendlyAppointmentApiResponse,
  CalendlyAppointmentsResponse,
  CalendlyAppointmentsApiResponse,
  CreateCalendlyAppointmentRequest,
  CreateCalendlyAppointmentApiRequest,
  LinkPatientToAppointmentRequest,
  LinkPatientToAppointmentApiRequest,
  GetCalendlyAppointmentsParams,
  CalendlyAppointmentErrorCode,
} from '@/types/calendlyAppointments';

/**
 * Calendly API endpoints
 */
const ENDPOINTS = {
  APPOINTMENTS: '/appointments/calendly/',
  APPOINTMENT_BY_ID: '/appointments/calendly/:id',
  LINK_PATIENT: '/appointments/calendly/:id/link-patient',
  REFRESH: '/appointments/calendly/:id?ignore_cache=true',
} as const;

/**
 * CalendlyDomain Service
 *
 * Provides Calendly appointment operations with enhanced error handling,
 * retry logic, and request deduplication.
 */
export class CalendlyDomain extends BaseDomain<CalendlyAppointmentApiError> {
  constructor() {
    const apiClient = new ApiClient<CalendlyAppointmentApiError>(
      (message, status, fieldErrors, code) =>
        new CalendlyAppointmentApiError(message, status, fieldErrors, code as CalendlyAppointmentErrorCode)
    );

    super(apiClient, tokenManager, requestManager);
  }

  /**
   * Get domain name for error messages
   */
  protected getDomainName(): string {
    return 'Calendly';
  }

  /**
   * Create domain-specific error instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): CalendlyAppointmentApiError {
    return new CalendlyAppointmentApiError(message, status, fieldErrors, code as CalendlyAppointmentErrorCode);
  }

  /**
   * Get current user's appointments with pagination and filtering
   *
   * @param params Pagination and filtering parameters
   * @returns Promise<CalendlyAppointmentsResponse> Paginated appointments data
   * @throws CalendlyAppointmentApiError When request fails or validation errors occur
   */
  async getMyAppointments(params: GetCalendlyAppointmentsParams = {}): Promise<CalendlyAppointmentsResponse> {
    return this.executeWithDeduplication('getMyAppointments', async () => {
      return this.executeWithRetry(async () => {
        // Validate pagination parameters
        const validation = PaginationService.validate(params);
        if (!validation.isValid) {
          throw this.createError(
            'getMyAppointments',
            'Invalid pagination parameters',
            400,
            this.buildFieldErrorsFromValidation(validation.errors),
            'INVALID_EVENT_URI'
          );
        }

        try {
          // Build query string and endpoint
          const queryString = PaginationService.buildQuery(params);
          const endpoint = ENDPOINTS.APPOINTMENTS + queryString;

          // Make API request
          const response = await this.apiClient.get<CalendlyAppointmentsApiResponse>(endpoint);

          // Transform response to standard format
          return {
            data: response.appointments.map(appointment => this.transformAppointmentFromApi(appointment)),
            pagination: {
              page: response.page,
              perPage: response.per_page,
              total: response.total,
              totalPages: response.total_pages,
            },
          };
        } catch (error) {
          throw this.handleApiError(error);
        }
      });
    });
  }

  /**
   * Get appointment by ID
   *
   * @param appointmentId Appointment UUID
   * @returns Promise<CalendlyAppointment> Appointment data
   * @throws CalendlyAppointmentApiError When request fails or appointment not found
   */
  async getAppointmentById(appointmentId: string): Promise<CalendlyAppointment> {
    return this.executeWithRetry(async () => {
      // Input validation
      if (!appointmentId || !this.isValidUuid(appointmentId)) {
        throw this.createError(
          'getAppointmentById',
          'Valid appointment ID is required',
          400,
          { appointmentId: 'Must be a valid UUID' },
          'APPOINTMENT_NOT_FOUND'
        );
      }

      try {
        // Build endpoint URL
        const endpoint = ENDPOINTS.APPOINTMENT_BY_ID.replace(':id', appointmentId);

        // Make API request
        const response = await this.apiClient.get<CalendlyAppointmentApiResponse>(endpoint);

        // Transform and return
        return this.transformAppointmentFromApi(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  /**
   * Create appointment from Calendly event
   *
   * @param request Appointment creation request
   * @returns Promise<CalendlyAppointment> Created appointment data
   * @throws CalendlyAppointmentApiError When request fails or validation errors occur
   */
  async createAppointment(request: CreateCalendlyAppointmentRequest): Promise<CalendlyAppointment> {
    return this.executeWithRetry(async () => {
      // Input validation
      if (!request.eventUri || !request.dieticianId) {
        throw this.createError(
          'createAppointment',
          'Event URI and dietician ID are required',
          400,
          {},
          'INVALID_EVENT_URI'
        );
      }

      // Transform request to API format (camelCase → snake_case)
      const apiRequest: CreateCalendlyAppointmentApiRequest = {
        event_uri: request.eventUri,
        dietician_id: request.dieticianId,
      };

      try {
        // Make API request
        const response = await this.apiClient.post<CalendlyAppointmentApiResponse>(
          ENDPOINTS.APPOINTMENTS,
          apiRequest
        );

        // Transform response (snake_case → camelCase)
        return this.transformAppointmentFromApi(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  /**
   * Link appointment to patient
   *
   * @param appointmentId Appointment UUID
   * @param request Patient linking request
   * @returns Promise<CalendlyAppointment> Updated appointment data
   * @throws CalendlyAppointmentApiError When request fails or validation errors occur
   */
  async linkAppointmentToPatient(
    appointmentId: string,
    request: LinkPatientToAppointmentRequest
  ): Promise<CalendlyAppointment> {
    return this.executeWithRetry(async () => {
      // Input validation
      if (!appointmentId || !this.isValidUuid(appointmentId)) {
        throw this.createError(
          'linkAppointmentToPatient',
          'Valid appointment ID is required',
          400,
          { appointmentId: 'Must be a valid UUID' },
          'APPOINTMENT_NOT_FOUND'
        );
      }

      if (!request.patientId || !this.isValidUuid(request.patientId)) {
        throw this.createError(
          'linkAppointmentToPatient',
          'Valid patient ID is required',
          400,
          { patientId: 'Must be a valid UUID' },
          'PATIENT_NOT_FOUND'
        );
      }

      // Transform request
      const apiRequest: LinkPatientToAppointmentApiRequest = {
        patient_id: request.patientId,
      };

      try {
        // Build endpoint URL
        const endpoint = ENDPOINTS.LINK_PATIENT.replace(':id', appointmentId);

        // Make API request
        const response = await this.apiClient.patch<CalendlyAppointmentApiResponse>(
          endpoint,
          apiRequest
        );

        // Transform and return
        return this.transformAppointmentFromApi(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  /**
   * Refresh appointment data from Calendly
   *
   * @param appointmentId Appointment UUID
   * @returns Promise<CalendlyAppointment> Refreshed appointment data
   * @throws CalendlyAppointmentApiError When request fails or appointment not found
   */
  async refreshAppointment(appointmentId: string): Promise<CalendlyAppointment> {
    const logger = getLoggerWithPrefix('CalendlyDomain');
    return this.executeWithRetry(async () => {
      // Input validation
      if (!appointmentId || !this.isValidUuid(appointmentId)) {
        throw this.createError(
          'refreshAppointment',
          'Valid appointment ID is required',
          400,
          { appointmentId: 'Must be a valid UUID' },
          'APPOINTMENT_NOT_FOUND'
        );
      }

      try {
        // Build endpoint URL
        const endpoint = ENDPOINTS.REFRESH.replace(':id', appointmentId);
        logger.debug('endpoint: ', endpoint);
        // Make API request with longer timeout for Calendly API calls
        const response = await this.apiClient.get<CalendlyAppointmentApiResponse>(endpoint);

        // Transform and return
        return this.transformAppointmentFromApi(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  /**
   * Transform API response to frontend format (snake_case → camelCase)
   *
   * @private
   */
  private transformAppointmentFromApi(apiResponse: CalendlyAppointmentApiResponse): CalendlyAppointment {
    // Validate required fields
    if (!apiResponse.appointment_id || !apiResponse.event_uri) {
      throw this.createError(
        'transformAppointmentFromApi',
        'Invalid API response: missing required fields',
        500,
        {},
        'UNKNOWN_ERROR'
      );
    }

    // Transform snake_case to camelCase
    return {
      appointmentId: apiResponse.appointment_id,
      eventUri: apiResponse.event_uri,
      eventUuid: apiResponse.event_uuid,
      userCalendarId: apiResponse.user_calendar_id,
      dieticianId: apiResponse.dietician_id,
      patientId: apiResponse.patient_id,
      eventName: apiResponse.event_name,
      eventStatus: apiResponse.event_status,
      startTime: apiResponse.start_time,
      endTime: apiResponse.end_time,
      eventTypeUri: apiResponse.event_type_uri,
      locationType: apiResponse.location_type,
      locationValue: apiResponse.location_value,
      patientEmail: apiResponse.patient_email,
      patientName: apiResponse.patient_name,
      patientFirstName: apiResponse.patient_first_name,
      patientLastName: apiResponse.patient_last_name,
      patientTimezone: apiResponse.patient_timezone,
      cancelUrl: apiResponse.cancel_url,
      rescheduleUrl: apiResponse.reschedule_url,
      inviteeUri: apiResponse.invitee_uri,
      inviteeStatus: apiResponse.invitee_status,
      rescheduled: apiResponse.rescheduled,
      noShow: apiResponse.no_show,
      eventCreatedAt: apiResponse.event_created_at,
      eventUpdatedAt: apiResponse.event_updated_at,
      inviteeCreatedAt: apiResponse.invitee_created_at,
      inviteeUpdatedAt: apiResponse.invitee_updated_at,
      dieticianCalendlyUserUri: apiResponse.dietician_calendly_user_uri,
      dieticianCalendlyEmail: apiResponse.dietician_calendly_email,
      dieticianCalendlyName: apiResponse.dietician_calendly_name,
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at,
      lastFetchedAt: apiResponse.last_fetched_at,
      paymentGuidance: apiResponse.payment_guidance ?? null,
    };
  }

  /**
   * Get authentication token from token manager
   *
   * @private
   */
  private getAuthToken(): string | null {
    return tokenManager.getToken();
  }

  /**
   * Handle API errors and transform to domain-specific errors
   *
   * @private
   */
  private handleApiError(error: unknown): CalendlyAppointmentApiError {
    // If already our error type, return as-is
    if (error instanceof CalendlyAppointmentApiError) {
      return error;
    }

    // Handle fetch/network errors
    if (error instanceof Error) {
      const message =
        typeof (error as unknown as { message?: unknown })?.message === 'string'
          ? (error as unknown as { message: string }).message
          : '';

      if (error.name === 'AbortError') {
        return this.createError(
          'handleApiError',
          'Request timeout. Please try again.',
          undefined,
          {},
          'TIMEOUT'
        );
      }

      if (message.includes('Failed to fetch')) {
        return this.createError(
          'handleApiError',
          'Network error. Please check your connection.',
          undefined,
          {},
          'NETWORK_ERROR'
        );
      }

      return this.createError(
        'handleApiError',
        message || 'An unexpected error occurred',
        undefined,
        {},
        'UNKNOWN_ERROR'
      );
    }

    // Handle unknown errors
    return this.createError(
      'handleApiError',
      'An unexpected error occurred',
      undefined,
      {},
      'UNKNOWN_ERROR'
    );
  }

  /**
   * Validate UUID format
   *
   * @private
   */
  private isValidUuid(uuid: string): boolean {
    const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return pattern.test(uuid);
  }

  /**
   * Build field errors from validation errors
   *
   * @private
   */
  private buildFieldErrorsFromValidation(errors: string[]): Record<string, string> {
    const fieldErrors: Record<string, string> = {};
    for (const error of errors) {
      if (error.includes('Page number')) {
        fieldErrors.page = error;
      } else if (error.includes('Page size')) {
        fieldErrors.perPage = error;
      } else if (error.includes('Sort order')) {
        fieldErrors.sortOrder = error;
      } else {
        fieldErrors.general = error;
      }
    }
    return fieldErrors;
  }
}

// Export domain instance
export const calendlyDomain = new CalendlyDomain();